/**
 * @description Super Admin SO app custom domain connect flow (LYN-145)
 *
 * Mirrors merchant ConnectDomainPanel UX: domain input, CNAME rows with copy,
 * verify polling, disconnect — for app.{base}, portal.{base}, and *.sites.{base}.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Globe,
  Link2,
  Loader2,
  RefreshCw,
  Trash2
} from 'lucide-react';
import {
  disconnectTenantCustomDomain,
  getTenantCustomDomainStatus,
  registerTenantCustomDomain,
  verifyTenantCustomDomain
} from '../../services/api/adminApi';
import { getSoOccupancyDoorUrl } from '../../config/domains';

const POLL_INTERVAL_MS = 30000;

const STEPS = {
  DOMAIN: 'domain',
  DNS: 'dns',
  CONNECTED: 'connected'
};

/**
 * Prefer API errorMessage / structured error object over raw JSON blobs.
 * @param {object|null} payload - register/status data
 * @returns {string|null}
 */
function resolveDomainErrorText(payload) {
  if (!payload) {
    return null;
  }
  if (typeof payload.errorMessage === 'string' && payload.errorMessage.trim()) {
    return payload.errorMessage;
  }
  const err = payload.error;
  if (!err) {
    return null;
  }
  if (typeof err === 'string') {
    try {
      const parsed = JSON.parse(err);
      if (parsed?.message) {
        return parsed.message;
      }
    } catch {
      return err;
    }
    return err;
  }
  if (typeof err === 'object' && err.message) {
    if (Array.isArray(err.details) && err.details.length > 0) {
      const detailText = err.details
        .map((entry) => {
          const host = entry.hostname || entry.type || 'hostname';
          const detail = typeof entry.error === 'string'
            ? entry.error
            : (entry.error?.message || entry.status || 'failed');
          return `${host}: ${detail}`;
        })
        .join('; ');
      return `${err.message} (${detailText})`;
    }
    return err.message;
  }
  return null;
}

function registrationPersisted(data) {
  if (!data) {
    return false;
  }
  return Boolean(
    data.domainBase
    || data.cnameInstructions
    || data.status === 'pending'
    || data.status === 'failed'
  );
}

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
      aria-label={label}
    >
      {copied ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </button>
  );
};

const DnsRecordRow = ({ record }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {record.type} record
      </span>
      <span className="text-xs text-gray-500">{record.label}</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">Host / Name</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-gray-900 break-all">{record.hostname}</code>
          <CopyButton value={record.hostname} label="Copy hostname" />
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">Points to / Value</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-gray-900 break-all">{record.value}</code>
          <CopyButton value={record.value} label="Copy target" />
        </div>
      </div>
    </div>
  </div>
);

function statusBadgeClass(status) {
  if (status === 'verified') {
    return 'bg-green-50 text-green-700';
  }
  if (status === 'failed') {
    return 'bg-red-50 text-red-700';
  }
  if (status === 'pending') {
    return 'bg-yellow-50 text-yellow-700';
  }
  return 'bg-gray-100 text-gray-600';
}

/**
 * @param {object} props
 * @param {string} props.tenantId - SO UUID
 * @param {string|null} props.slug - Lynx subdomain slug
 * @param {object|null} [props.domain] - Tenant domain payload (occupancyHost)
 * @param {() => Promise<void>} [props.onDomainChange] - refresh tenant detail after mutations
 */
export default function SOCustomDomainPanel({ tenantId, slug, domain = null, onDomainChange }) {
  const [step, setStep] = useState(STEPS.DOMAIN);
  const [domainInput, setDomainInput] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const domainStatus = status?.status || 'none';
  const isVerified = domainStatus === 'verified';
  const isPending = domainStatus === 'pending';
  const isFailed = domainStatus === 'failed';
  const cnameInstructions = status?.cnameInstructions;

  const loadStatus = useCallback(async () => {
    setError(null);
    try {
      const result = await getTenantCustomDomainStatus(tenantId);
      if (result.success) {
        setStatus(result.data);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load domain status');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (isVerified) {
      setStep(STEPS.CONNECTED);
      return;
    }
    if (isPending || isFailed) {
      setStep(STEPS.DNS);
      if (status?.customDomainBase && !domainInput) {
        setDomainInput(status.customDomainBase);
      }
      return;
    }
    setStep(STEPS.DOMAIN);
  }, [domainInput, isFailed, isPending, isVerified, status?.customDomainBase]);

  useEffect(() => {
    if (!isPending) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      loadStatus();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isPending, loadStatus]);

  const handleRegister = async (event) => {
    event.preventDefault();
    const trimmed = domainInput.trim().toLowerCase();
    if (!trimmed) {
      return;
    }

    setRegistering(true);
    setActionError(null);
    try {
      const result = await registerTenantCustomDomain(tenantId, { domainBase: trimmed });
      const data = result?.data || result;
      // Partial CF failure still persists the domain (HTTP 200, success:false).
      // Always enter DNS handoff when the server saved domainBase / instructions.
      if (registrationPersisted(data)) {
        await loadStatus();
        if (onDomainChange) {
          await onDomainChange();
        }
        setStep(STEPS.DNS);
        if (result.success === false) {
          setActionError(
            resolveDomainErrorText(data)
            || result.error?.message
            || 'Registration completed with hostname errors. Review DNS steps and retry or disconnect.'
          );
        }
      } else if (result.success === false) {
        setActionError(
          result.error?.message
          || resolveDomainErrorText(data)
          || 'Registration failed'
        );
      } else {
        await loadStatus();
        if (onDomainChange) {
          await onDomainChange();
        }
        setStep(STEPS.DNS);
      }
    } catch (err) {
      setActionError(err.response?.data?.error?.message || 'Failed to register domain');
    } finally {
      setRegistering(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setActionError(null);
    try {
      await verifyTenantCustomDomain(tenantId);
      await loadStatus();
      if (onDomainChange) {
        await onDomainChange();
      }
    } catch (err) {
      setActionError(err.response?.data?.error?.message || 'Failed to check domain status');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setActionError(null);
    try {
      await disconnectTenantCustomDomain(tenantId);
      setDomainInput('');
      setShowDisconnectConfirm(false);
      await loadStatus();
      if (onDomainChange) {
        await onDomainChange();
      }
    } catch (err) {
      setActionError(err.response?.data?.error?.message || 'Failed to disconnect domain');
    } finally {
      setDisconnecting(false);
    }
  };

  const defaultHostname = getSoOccupancyDoorUrl(domain || { slug }) || null;

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-red-800">{error}</p>
          <button
            type="button"
            onClick={loadStatus}
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-red-900 hover:underline"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-50 p-2.5 flex-shrink-0">
          <Globe className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">App Custom Domain</h3>
            {domainStatus !== 'none' && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(domainStatus)}`}>
                {domainStatus}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Route merchant dashboard and employee portal on the SO&apos;s own domain (
            <span className="font-mono text-gray-800">app.example.com</span>
            {' '}
            and
            {' '}
            <span className="font-mono text-gray-800">portal.example.com</span>
            ). Share the DNS records below with the SO — Lynx does not manage their DNS.
            {defaultHostname && (
              <>
                {' '}
                Default Lynx URLs remain at{' '}
                <span className="font-mono text-gray-800">{defaultHostname}</span>
                {isVerified ? ' (slug redirects when verified).' : '.'}
              </>
            )}
          </p>
        </div>
      </div>

      {actionError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{actionError}</span>
        </div>
      )}

      {step === STEPS.CONNECTED && status?.customDomainBase && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-base font-semibold text-green-900">Domain verified</h4>
            <p className="text-sm text-green-800 mt-1">
              <span className="font-mono">{status.subdomains?.dashboard}</span>
              {' '}
              and
              {' '}
              <span className="font-mono">{status.subdomains?.employee}</span>
              {' '}
              are active. Verified
              {' '}
              {status.verifiedAt ? new Date(status.verifiedAt).toLocaleString() : 'recently'}.
            </p>
            {!showDisconnectConfirm ? (
              <button
                type="button"
                onClick={() => setShowDisconnectConfirm(true)}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                <Trash2 className="h-4 w-4" />
                Disconnect domain
              </button>
            ) : (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-green-900">Remove Cloudflare hostnames and clear config?</span>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-500 disabled:opacity-50"
                >
                  {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Confirm disconnect
                </button>
                <button
                  type="button"
                  onClick={() => setShowDisconnectConfirm(false)}
                  disabled={disconnecting}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {step === STEPS.DOMAIN && (
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label htmlFor="so-custom-domain-base" className="block text-sm font-medium text-gray-700">
              Domain base (apex)
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Enter the root domain the SO owns, e.g.{' '}
              <span className="font-mono">acmepayments.com</span>.
              Lynx will use
              {' '}
              <span className="font-mono">app.</span>
              and
              {' '}
              <span className="font-mono">portal.</span>
              subdomains on that base.
            </p>
            <input
              id="so-custom-domain-base"
              type="text"
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              placeholder="example.com"
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-mono shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <button
            type="submit"
            disabled={registering || !domainInput.trim()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {registering ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Registering…
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                Register domain
              </>
            )}
          </button>
        </form>
      )}

      {step === STEPS.DNS && cnameInstructions && (
        <div className="space-y-4">
          {(isPending || isFailed) && (
            <div className={`rounded-lg border p-4 flex items-start gap-3 ${
              isFailed ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
            }`}>
              {isPending ? (
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-medium ${isFailed ? 'text-red-900' : 'text-blue-900'}`}>
                  {isFailed ? 'Registration issue' : 'Waiting for DNS'}
                </p>
                <p className={`text-sm mt-1 ${isFailed ? 'text-red-800' : 'text-blue-800'}`}>
                  {isFailed
                    ? (
                      resolveDomainErrorText(status)
                      || 'One or more hostnames failed to register. Retry or disconnect and try again.'
                    )
                    : 'Send these CNAME records to the SO for their DNS provider. Verification continues automatically every 30 seconds, or use Check status.'}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 p-4 space-y-3">
            <p className="text-sm text-gray-700">
              Add CNAME records pointing to{' '}
              <span className="font-mono font-medium">{cnameInstructions.target}</span>.
              Use DNS-only mode (grey cloud in Cloudflare) — do not proxy these records.
            </p>
            {cnameInstructions.records.map((record) => (
              <DnsRecordRow key={record.hostname} record={record} />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleVerify}
              disabled={verifying}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 text-sm font-medium disabled:opacity-60"
            >
              {verifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Check status
                </>
              )}
            </button>

            {!showDisconnectConfirm ? (
              <button
                type="button"
                onClick={() => setShowDisconnectConfirm(true)}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Disconnect
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="text-sm font-medium text-red-700 hover:text-red-800 disabled:opacity-50"
                >
                  {disconnecting ? 'Removing…' : 'Confirm disconnect'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
